import { Link, Navigate, useParams } from "react-router-dom";
import AccessControl from "../pages/AccessControl";
import BackgroundChecks from "../pages/BackgroundChecks";
import ChatSpace from "../pages/ChatSpace";
import EmailMessages from "../pages/EmailMessages";
import Feedback from "../pages/Feedback";
import Help from "../pages/Help";
import Invitations from "../pages/Invitations";
import JobApplications from "../pages/JobApplications";
import JobPosts from "../pages/JobPosts";
import NewJobPost from "../pages/NewJobPost";
import OrganizationDetailsPage from "../pages/OrganizationDetailsPage";
import OrganizationsPage from "../pages/OrganizationsPage";
import PartnerCommissions from "../pages/PartnerCommissions";
import Partners from "../pages/Partners";
import ProfessionalProfiles from "../pages/ProfessionalProfiles";
import ProfessionalProfileDetails from "../pages/ProfessionalProfileDetails";
import Reports from "../pages/Reports";
import ScheduledInterviews from "../pages/ScheduledInterviews";
import Settings from "../pages/Settings";
import Support from "../pages/Support";
import TalentSearch from "../pages/TalentSearch";
import WebsiteCreationRedirect from "../pages/WebsiteCreationRedirect";
import WebsiteDetails from "../pages/WebsiteDetails";
import UserProfilePage from "../users/profile/UserProfilePage";
import AssessmentProcessDetailPage from "../pages/care/AssessmentProcessDetailPage";
import AssessmentRecordDetailPage from "../pages/care/AssessmentRecordDetailPage";
import AssessmentRecordEditPage from "../pages/care/AssessmentRecordEditPage";
import AssessmentWeeklyReportsPage from "../pages/care/AssessmentWeeklyReportsPage";
import AssessmentsPage from "../pages/care/AssessmentsPage";
import CareProfilePage from "../pages/care/CareProfilePage";
import CareStaffAttendancePage from "../pages/care/CareStaffAttendancePage";
import CareStaffDetailsPage from "../pages/care/CareStaffDetailsPage";
import CareStaffSnapshotPage from "../pages/care/CareStaffSnapshotPage";
import CareStaffProfilesPage from "../pages/care/CareStaffProfilesPage";
import CareStaffSchedulesPage from "../pages/care/CareStaffSchedulesPage";
import CareStaffTimesheetsChartPage from "../pages/care/CareStaffTimesheetsChartPage";
import CareStaffTimesheetsPage from "../pages/care/CareStaffTimesheetsPage";
import ComplianceDashboardPage from "../pages/care/ComplianceDashboardPage";
import ComplianceInventoryPage from "../pages/care/ComplianceInventoryPage";
import EmergencyEvacuationDrillsPage from "../pages/care/EmergencyEvacuationDrillsPage";
import EfaxPage from "../pages/care/EfaxPage";
import HomeActivityPlannerPage from "../pages/care/HomeActivityPlannerPage";
import MyPaymentsPage from "../pages/care/MyPaymentsPage";
import PayrollPage from "../pages/care/PayrollPage";
import {
  default as ShiftFillerTasksPage,
  TaskDetailsPage as ShiftFillerTaskDetailsPage,
  TaskStagePage as ShiftFillerTaskStagePage,
} from "../pages/care/OlitaskMyTasks";
import DashboardPage from "../pages/care/DashboardPage";
import DailyNotesPage from "../pages/care/DailyNotesPage";
import HomePage from "../pages/care/HomePage";
import NegotiatedCarePlanPage from "../pages/care/NegotiatedCarePlanPage";
import NewResidentAssessmentPage from "../pages/care/NewResidentAssessmentPage";
import NewResidentPage from "../pages/care/NewResidentPage";
import OCareFeaturePage from "../pages/care/OCareFeaturePage";
import PharmaciesPage from "../pages/care/PharmaciesPage";
import ResidentClockInOutPage from "../pages/care/ResidentClockInOutPage";
import ResidentDetailPage from "../pages/care/ResidentDetailPage";
import ResidentMedicationsPage from "../pages/care/ResidentMedicationsPage";
import ResidentsListPage from "../pages/care/ResidentsListPage";
import ShiftFillerCreatePage from "../pages/care/ShiftFillerCreatePage";
import { GeneralTaskCreatePage } from "../pages/care/TaskCreatePage";
import WorkspaceChargesPage from "../workspaces/WorkspaceChargesPage";
import WorkspaceCreatePage from "../workspaces/WorkspaceCreatePage";
import WorkspaceDetailsPage from "../workspaces/WorkspaceDetailsPage";
import WorkspaceDownloadsPage from "../workspaces/WorkspaceDownloadsPage";
import WorkspaceInvitationAcceptPage from "../workspaces/WorkspaceInvitationAcceptPage";
import WorkspaceInvitations from "../workspaces/WorkspaceInvitations";
import WorkspaceMemberDetailsPage from "../workspaces/WorkspaceMemberDetailsPage";
import WorkspaceOrganizationPage from "../workspaces/WorkspaceOrganizationPage";
import WorkspaceNotificationsPage from "../workspaces/WorkspaceNotificationsPage";
import WorkspacePaymentMethodsPage from "../workspaces/WorkspacePaymentMethodsPage";
import WorkspaceSubscriptionsPage from "../workspaces/WorkspaceSubscriptionsPage";
import WorkspacesPage from "../workspaces/WorkspacesPage";
import { CareShell, Panel } from "../components/care/CareLayout";
import RequireCareRole from "../components/routing/RequireCareRole";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspace } from "../contexts/WorkspaceContext";
import useWorkspaceSubscriptionFeatures from "../hooks/useWorkspaceSubscriptionFeatures";
import {
  hasSubscriptionFeature,
  SUBSCRIPTION_FEATURE_KEYS,
} from "../workspaces/subscriptionFeatures";
import WaterTemperatureReadingsPage from "pages/care/WaterTemperatureReadingsPage";
import CareLayoutRoute from "./CareLayoutRoute";
import FacilityAuditToolsPage from "pages/care/FacilityAuditToolsPage";
import WorkspaceLicenseFeePage from "pages/care/WorkspaceLicenseFeePage";
import PolicyQuestionnaireRoute from "pages/care/PolicyQuestionnaireRoute";
import { CARE_VIEW_INCIDENTS, hasWorkspacePrivilege } from "../utils/carePrivileges";

const normalizeRouteRole = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const isCareOwnerOrAdmin = (roles = []) => {
  const normalizedRoles = roles.map(normalizeRouteRole);
  return (
    normalizedRoles.includes("care_owner") ||
    normalizedRoles.includes("care_admin")
  );
};

function SubscriptionUpgradePrompt() {
  return (
    <CareShell className="dashboard-content app-page-content">
      <Panel title="Upgrade subscription">
        <div className="subscription-upgrade-prompt">
          <span className="round-icon amber">
            <i className="ti ti-lock" />
          </span>
          <div>
            <h2>Upgrade your subscription to access this feature.</h2>
            <p>
              This organizational workspace needs a Standard or Premium
              subscription before these care tools are available.
            </p>
            <Link
              className="text-action"
              to="/workspaces/current/subscriptions"
            >
              Manage subscription <i className="ti ti-arrow-right" />
            </Link>
          </div>
        </div>
      </Panel>
    </CareShell>
  );
}

function CurrentWorkspaceRedirect({ section }) {
  const { selectedWorkspaceId, loading, workspaceListReady } = useWorkspace();

  if (loading || !workspaceListReady) return null;
  if (!selectedWorkspaceId) return <Navigate to="/workspaces" replace />;
  return (
    <Navigate to={`/workspaces/${selectedWorkspaceId}/${section}`} replace />
  );
}

function RequireWorkspaceMembership({ children }) {
  const { workspaceId } = useParams();
  const { workspaces, loading, workspaceListReady } = useWorkspace();

  if (loading || !workspaceListReady) return null;

  const isMemberWorkspace = workspaces.some(
    (workspace) => workspace?.id === workspaceId,
  );

  if (!isMemberWorkspace) {
    return <Navigate to="/workspaces" replace />;
  }

  return children;
}

function ShellRoute({ children }) {
  return (
    <CareShell className="dashboard-content app-page-content">
      {children}
    </CareShell>
  );
}

function OwnerAdminRoute({ children }) {
  return (
    <RequireCareRole roles={["care_owner", "care_admin"]}>
      {children}
    </RequireCareRole>
  );
}

const ownerAdmin = (element) => <OwnerAdminRoute>{element}</OwnerAdminRoute>;

function IndividualWorkspaceRoute({ children }) {
  const { isIndividualWorkspace, loading, workspaceListReady } = useWorkspace();

  if (loading || !workspaceListReady) return null;
  if (!isIndividualWorkspace) return <Navigate to="/dashboard" replace />;

  return children;
}

const individualWorkspaceOnly = (element) => (
  <IndividualWorkspaceRoute>{element}</IndividualWorkspaceRoute>
);

function IncidentPrivilegeRoute({ children }) {
  const { user, privileges = [] } = useAuth();
  const { selectedWorkspace, loading, workspaceListReady } = useWorkspace();

  if (loading || !workspaceListReady) return null;
  if (!hasWorkspacePrivilege({ privileges, user, workspace: selectedWorkspace }, CARE_VIEW_INCIDENTS)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function SubscriptionFeatureRoute({
  children,
  feature,
  redirectTo = "/dashboard",
}) {
  const { careRoles = [] } = useAuth();
  const { isOrganizationWorkspace, loading, workspaceListReady } =
    useWorkspace();
  const {
    features,
    loading: subscriptionLoading,
    tier,
  } = useWorkspaceSubscriptionFeatures();

  const shouldShowUpgradePrompt =
    isOrganizationWorkspace &&
    !["STANDARD", "PREMIUM"].includes(String(tier || "").toUpperCase()) &&
    isCareOwnerOrAdmin(careRoles);

  if (loading || !workspaceListReady || subscriptionLoading) return null;
  if (!hasSubscriptionFeature(features, feature)) {
    if (shouldShowUpgradePrompt) return <SubscriptionUpgradePrompt />;
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

const subscriptionFeatureOnly = (feature, element, redirectTo) => (
  <SubscriptionFeatureRoute feature={feature} redirectTo={redirectTo}>
    {element}
  </SubscriptionFeatureRoute>
);

const workspaceMemberOnly = (element) => (
  <RequireWorkspaceMembership>{element}</RequireWorkspaceMembership>
);

function AssessmentLegacyRedirect() {
  const { assessmentId } = useParams();
  return (
    <Navigate
      to={`/assessments/${encodeURIComponent(assessmentId || "")}/new`}
      replace
    />
  );
}

function FacilityAuditToolsRoute() {
  const { features } = useWorkspaceSubscriptionFeatures();
  return (
    <FacilityAuditToolsPage
      hasFeature={(key) => hasSubscriptionFeature(features, key)}
    />
  );
}

const careRoutes = [
  { path: "/home", element: <HomePage /> },
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/care-profile", element: <CareProfilePage /> },
  { path: "/assessments", element: <AssessmentsPage /> },
  { path: "/daily-notes", element: <DailyNotesPage /> },
  {
    path: "/assessments/new",
    element: ownerAdmin(<NewResidentAssessmentPage />),
  },
  {
    path: "/assessments/:assessmentId/records/:recordId/edit",
    element: <AssessmentRecordEditPage />,
  },
  {
    path: "/assessments/:assessmentId/records/:recordId",
    element: <AssessmentRecordDetailPage />,
  },
  {
    path: "/assessments/:assessmentId/weekly-reports",
    element: <AssessmentWeeklyReportsPage />,
  },
  {
    path: "/assessments/:assessmentId/new",
    element: <AssessmentProcessDetailPage />,
  },
  {
    path: "/assessments/:assessmentId/current",
    element: <AssessmentProcessDetailPage />,
  },
  { path: "/assessments/:assessmentId", element: <AssessmentLegacyRedirect /> },
  { path: "/care-plans/negotiated", element: <NegotiatedCarePlanPage /> },
  {
    path: "/care-plans/negotiated/new-client/intake",
    element: <NegotiatedCarePlanPage screen="intake" />,
  },
  {
    path: "/care-plans/negotiated/new-client/meet-and-greet",
    element: <NegotiatedCarePlanPage screen="meet" />,
  },
  {
    path: "/care-plans/negotiated/new-client/preliminary-plan",
    element: <NegotiatedCarePlanPage screen="preliminary" />,
  },
  {
    path: "/care-plans/negotiated/new-client/probation",
    element: <NegotiatedCarePlanPage screen="probation" />,
  },
  {
    path: "/care-plans/negotiated/final-plan",
    element: <NegotiatedCarePlanPage screen="final" />,
  },
  {
    path: "/care-plans/negotiated/existing-client/annual-review",
    element: <NegotiatedCarePlanPage screen="annual" />,
  },
  {
    path: "/care-plans/negotiated/confirmed-files",
    element: <NegotiatedCarePlanPage screen="confirmed" />,
  },
  { path: "/residents", element: <ResidentsListPage /> },
  {
    path: "/resident-medications",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.medicationManagement,
      <ResidentMedicationsPage />,
    ),
  },
  {
    path: "/resident-vital-signs",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.residents,
      <ResidentMedicationsPage view="vitals" />,
    ),
  },
  {
    path: "/resident-care-plans",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.carePlans,
      <ResidentMedicationsPage view="carePlans" />,
    ),
  },
  {
    path: "/resident-adls",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.carePlans,
      <HomeActivityPlannerPage />,
    ),
  },
  {
    path: "/pharmacies",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.medicationManagement,
      <PharmaciesPage />,
    ),
  },
  {
    path: "/efax",
    element: <EfaxPage />,
  },
  { path: "/residents/new", element: ownerAdmin(<NewResidentPage />) },
  {
    path: "/residents/:residentId/onboarding",
    element: ownerAdmin(<NewResidentPage />),
  },
  { path: "/residents/:residentId", element: <ResidentDetailPage /> },
  {
    path: "/resident-clock-in-out",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.residents,
      <ResidentClockInOutPage />,
    ),
  },
  {
    path: "/staff",
    element: <CareStaffProfilesPage />,
  },
  {
    path: "/staff/:careProfileId",
    element: <CareStaffDetailsPage />,
  },
  {
    path: "/staff/:careProfileId/snapshots/:snapshotType",
    element: <CareStaffSnapshotPage />,
  },
  {
    path: "/fire-drills",
    element: <Navigate to="/emergency-evacuation-drills" replace />,
  },
  {
    path: "/emergency-evacuation-drills",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.emergencyEvacuationDrills,
      <EmergencyEvacuationDrillsPage />,
    ),
  },
  {
    path: "/water-temperature-readings",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.waterTemperatureReadings,
      <WaterTemperatureReadingsPage />,
    ),
  },
  {
    path: "/compliance/audit-tools",
    element: <FacilityAuditToolsRoute />,
  },
  {
    path: "/compliance",
    element: <ComplianceDashboardPage />,
  },
  {
    path: "/compliance/inventory",
    element: <ComplianceInventoryPage />,
  },
  {
    path: "/compliance/alerts",
    element: <OCareFeaturePage moduleKey="complianceAlerts" />,
  },
  {
    path: "/incidents",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.incidentReporting,
      <IncidentPrivilegeRoute><OCareFeaturePage moduleKey="incidents" /></IncidentPrivilegeRoute>,
    ),
  },
  {
    path: "/cbhs",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.cbhsSupportiveSupervisionTracking,
      <OCareFeaturePage moduleKey="cbhs" />,
    ),
  },
  {
    path: "/rn-delegations",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.rnDelegationWorkflows,
      <OCareFeaturePage moduleKey="rnDelegation" />,
    ),
  },
  {
    path: "/family-portal",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.familyPortal,
      <OCareFeaturePage moduleKey="familyPortal" />,
    ),
  },
  {
    path: "/secure-care-team-exchange",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.secureCareTeamDocumentExchange,
      <OCareFeaturePage moduleKey="secureExchange" />,
    ),
  },
  {
    path: "/care-planning",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.mealPlanning,
      <OCareFeaturePage moduleKey="carePlanning" />,
    ),
  },
  {
    path: "/home-activity-planner",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.activityPlanning,
      <HomeActivityPlannerPage />,
    ),
  },
  {
    path: "/hours-operations",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.weeklyCaregiverHoursTracking,
      <OCareFeaturePage moduleKey="hoursOperations" />,
    ),
  },
  {
    path: "/staff/:careProfileId/timesheets",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.timesheets,
      <CareStaffTimesheetsPage />,
      "/staff",
    ),
  },
  {
    path: "/attendance",
    element: ownerAdmin(<CareStaffAttendancePage />),
  },
  {
    path: "/schedules",
    element: <CareStaffSchedulesPage />,
  },
  {
    path: "/my-attendance",
    element: <CareStaffAttendancePage scope="mine" />,
  },
  {
    path: "/my-schedule",
    element: <CareStaffSchedulesPage scope="mine" />,
  },
  {
    path: "/my-attendance/records",
    element: <CareStaffAttendancePage scope="mine" mode="records" />,
  },
  {
    path: "/timesheets",
    element: ownerAdmin(
      subscriptionFeatureOnly(
        SUBSCRIPTION_FEATURE_KEYS.timesheets,
        <CareStaffTimesheetsPage />,
      ),
    ),
  },
  {
    path: "/timesheets/chart",
    element: ownerAdmin(
      subscriptionFeatureOnly(
        SUBSCRIPTION_FEATURE_KEYS.timesheets,
        <CareStaffTimesheetsChartPage />,
      ),
    ),
  },
  {
    path: "/my-timesheet",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.timesheets,
      <CareStaffTimesheetsPage scope="mine" />,
    ),
  },
  {
    path: "/my-timesheet/chart",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.timesheets,
      <CareStaffTimesheetsChartPage scope="mine" />,
    ),
  },
  {
    path: "/license-fee",
    element: subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.annualLicenseFee,
      <WorkspaceLicenseFeePage />,
    ),
  },
  {
    path: "/compliance/questionnaires/:policyCode",
    element: <PolicyQuestionnaireRoute />,
  },
  {
    path: "/my-payments",
    element: <MyPaymentsPage />,
  },
  {
    path: "/payroll",
    element: ownerAdmin(subscriptionFeatureOnly(
      SUBSCRIPTION_FEATURE_KEYS.payroll,
      <PayrollPage />,
      "/home",
    )),
  },
];

const hireCareGiverRoutes = [
  {
    path: "/hire-care-giver",
    element: ownerAdmin(
      <Navigate to="/hire-care-giver/talent-search" replace />,
    ),
  },
  {
    path: "/hire-care-giver/talent-search",
    element: ownerAdmin(
      <ShellRoute>
        <TalentSearch />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/job-posts",
    element: ownerAdmin(
      <ShellRoute>
        <JobPosts />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/applications",
    element: ownerAdmin(
      <ShellRoute>
        <JobApplications />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/interviews",
    element: ownerAdmin(
      <ShellRoute>
        <ScheduledInterviews />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/job-posts/new",
    element: ownerAdmin(
      <ShellRoute>
        <NewJobPost />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/professional-profiles",
    element: ownerAdmin(
      individualWorkspaceOnly(
        <ShellRoute>
          <ProfessionalProfiles />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/hire-care-giver/professional-profiles/new",
    element: ownerAdmin(
      individualWorkspaceOnly(
        <ShellRoute>
          <ProfessionalProfileDetails forceAddMode />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/professional-profiles/new",
    element: ownerAdmin(
      individualWorkspaceOnly(
        <ShellRoute>
          <ProfessionalProfileDetails forceAddMode />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/professional-profiles/:profileId/edit",
    element: ownerAdmin(
      individualWorkspaceOnly(
        <ShellRoute>
          <ProfessionalProfileDetails startEditing />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/professional-profiles/:profileId",
    element: ownerAdmin(
      <ShellRoute>
        <ProfessionalProfileDetails />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/background-checks",
    element: ownerAdmin(
      <ShellRoute>
        <BackgroundChecks />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/reports",
    element: ownerAdmin(
      <ShellRoute>
        <Reports />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/shift-filler",
    element: ownerAdmin(
      <Navigate to="/hire-care-giver/shift-filler/posts" replace />,
    ),
  },
  {
    path: "/hire-care-giver/shift-filler/new",
    element: ownerAdmin(
      <ShellRoute>
        <ShiftFillerCreatePage />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/shift-filler/posts",
    element: ownerAdmin(
      <ShellRoute>
        <ShiftFillerTasksPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/shift-filler/posts/:taskId",
    element: ownerAdmin(
      <ShellRoute>
        <ShiftFillerTaskDetailsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/shift-filler/posts/:taskId/:stage",
    element: ownerAdmin(
      <ShellRoute>
        <ShiftFillerTaskStagePage />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/general-tasks",
    element: ownerAdmin(
      <Navigate to="/hire-care-giver/general-tasks/posts" replace />,
    ),
  },
  {
    path: "/hire-care-giver/general-tasks/new",
    element: ownerAdmin(
      <ShellRoute>
        <GeneralTaskCreatePage />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/general-tasks/posts",
    element: ownerAdmin(
      <ShellRoute>
        <ShiftFillerTasksPage
          basePath="/hire-care-giver/general-tasks/posts"
          createPath="/hire-care-giver/general-tasks/new"
          heading="General Tasks"
          taskKind="GENERAL"
        />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/general-tasks/posts/:taskId",
    element: ownerAdmin(
      <ShellRoute>
        <ShiftFillerTaskDetailsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/hire-care-giver/general-tasks/posts/:taskId/:stage",
    element: ownerAdmin(
      <ShellRoute>
        <ShiftFillerTaskStagePage />
      </ShellRoute>,
    ),
  },
  {
    path: "/talent-search",
    element: ownerAdmin(
      <Navigate to="/hire-care-giver/talent-search" replace />,
    ),
  },
  {
    path: "/job-posts",
    element: ownerAdmin(<Navigate to="/hire-care-giver/job-posts" replace />),
  },
  {
    path: "/job-applications",
    element: ownerAdmin(
      <Navigate to="/hire-care-giver/applications" replace />,
    ),
  },
  {
    path: "/interviews",
    element: ownerAdmin(<Navigate to="/hire-care-giver/interviews" replace />),
  },
  {
    path: "/job-posts/new",
    element: ownerAdmin(
      <Navigate to="/hire-care-giver/job-posts/new" replace />,
    ),
  },
  {
    path: "/professional-profiles",
    element: ownerAdmin(
      individualWorkspaceOnly(
        <Navigate to="/hire-care-giver/professional-profiles" replace />,
      ),
    ),
  },
  {
    path: "/background-checks",
    element: ownerAdmin(
      <Navigate to="/hire-care-giver/background-checks" replace />,
    ),
  },
  {
    path: "/reports",
    element: ownerAdmin(<Navigate to="/hire-care-giver/reports" replace />),
  },
];

const workspaceRoutes = [
  {
    path: "/workspaces",
    element: (
      <ShellRoute>
        <WorkspacesPage />
      </ShellRoute>
    ),
  },
  {
    path: "/workspaces/new",
    element: (
      <ShellRoute>
        <WorkspaceCreatePage />
      </ShellRoute>
    ),
  },
  {
    path: "/workspaces/invitations/accept",
    element: (
      <ShellRoute>
        <WorkspaceInvitationAcceptPage />
      </ShellRoute>
    ),
  },
  {
    path: "/workspaces/:workspaceId",
    element: workspaceMemberOnly(
      <ShellRoute>
        <WorkspaceDetailsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/workspaces/:workspaceId/invitations/:invitationId",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceInvitations />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/members/:userId",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceMemberDetailsPage />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/organization",
    element: workspaceMemberOnly(
      <ShellRoute>
        <WorkspaceOrganizationPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/workspaces/:workspaceId/notifications",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceNotificationsPage />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/current/payment-methods",
    element: ownerAdmin(<CurrentWorkspaceRedirect section="payment-methods" />),
  },
  {
    path: "/workspaces/current/charges",
    element: ownerAdmin(<CurrentWorkspaceRedirect section="charges" />),
  },
  {
    path: "/workspaces/current/subscriptions",
    element: ownerAdmin(<CurrentWorkspaceRedirect section="subscriptions" />),
  },
  {
    path: "/workspaces/:workspaceId/payment-methods",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspacePaymentMethodsPage />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/charges",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceChargesPage />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/subscriptions",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceSubscriptionsPage />
        </ShellRoute>,
      ),
    ),
  },
];

const appUtilityRoutes = [
  {
    path: "/profile",
    element: (
      <ShellRoute>
        <UserProfilePage />
      </ShellRoute>
    ),
  },
  {
    path: "/profile/:userId",
    element: (
      <ShellRoute>
        <UserProfilePage />
      </ShellRoute>
    ),
  },
  {
    path: "/invitations",
    element: (
      <ShellRoute>
        <Invitations />
      </ShellRoute>
    ),
  },
  {
    path: "/website",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteDetails />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/templates",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/build/:templateId",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/regenerate",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect targetPanel="templates" />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/generating/:websiteId",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/success/:websiteId",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/domain",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect targetPanel="domains" />
      </ShellRoute>,
    ),
  },
  {
    path: "/downloads",
    element: ownerAdmin(
      <ShellRoute>
        <WorkspaceDownloadsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/organizations",
    element: ownerAdmin(
      <ShellRoute>
        <OrganizationsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/organizations/:organizationId",
    element: ownerAdmin(
      <ShellRoute>
        <OrganizationDetailsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/access-control",
    element: ownerAdmin(
      <ShellRoute>
        <AccessControl />
      </ShellRoute>,
    ),
  },
  {
    path: "/settings",
    element: (
      <ShellRoute>
        <Settings />
      </ShellRoute>
    ),
  },
  {
    path: "/chat-space",
    element: (
      <ShellRoute>
        <ChatSpace />
      </ShellRoute>
    ),
  },
  {
    path: "/email-messages",
    element: (
      <ShellRoute>
        <EmailMessages />
      </ShellRoute>
    ),
  },
  {
    path: "/app/help",
    element: (
      <ShellRoute>
        <Help />
      </ShellRoute>
    ),
  },
  {
    path: "/support",
    element: (
      <ShellRoute>
        <Support />
      </ShellRoute>
    ),
  },
  {
    path: "/feedback",
    element: (
      <ShellRoute>
        <Feedback />
      </ShellRoute>
    ),
  },
  {
    path: "/partners",
    element: ownerAdmin(
      <ShellRoute>
        <Partners />
      </ShellRoute>,
    ),
  },
  {
    path: "/partners/commissions",
    element: ownerAdmin(
      <ShellRoute>
        <PartnerCommissions />
      </ShellRoute>,
    ),
  },
];

export const protectedRoutes = [
  {
    element: <CareLayoutRoute />,
    children: [
      ...careRoutes,
      ...hireCareGiverRoutes,
      ...workspaceRoutes,
      ...appUtilityRoutes,
    ],
  },
];
